import { useEffect, useMemo, useState } from "react";
import { formatMoney, formatPublicEventDate, workspaceNoun } from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";
import { RELEASE_FORM_ACTION_LABEL } from "./memberPortalUtils";
import {
  activeMaintenanceWindows,
  addMinutes,
  categorizeMemberResources,
  defaultMemberReservationWindow,
  formatReservationAccessLabel,
  maintenanceWindowTitle,
  memberReservationInputConstraints,
  memberReservationResourceName,
  memberReservationRuleSummary,
  memberResourceDisplayName,
  memberResourceKindLabel,
  memberResourceUnavailableReason,
  publicReservationStatusLabel,
  rangeOpeningStatusLabel,
  reservationAdvanceWindowLabel,
  reservationIsToday,
  reservationLimitUsage,
  resourceHasReservableWindow,
  resourceOpeningStatusLabel,
  resourceOpeningTimeLabel,
  sortMemberResources,
  toDateTimeLocalInput
} from "./memberReservationUtils";

function normalizedResourceKind(resource = {}) {
  return String(resource.kind || resource.resourceType || resource.type || "area").toLowerCase();
}

function resourceMatchesFilters(resource = {}, searchTerm = "", kindFilter = "all") {
  const kind = normalizedResourceKind(resource);
  const haystack = [
    memberResourceDisplayName(resource),
    memberResourceKindLabel(resource),
    resource.label,
    resource.name,
    resource.description
  ].filter(Boolean).join(" ").toLowerCase();
  const search = searchTerm.trim().toLowerCase();
  return (!search || haystack.includes(search)) && (kindFilter === "all" || kind === kindFilter);
}

function resourceKindOptions(resources = []) {
  return Array.from(resources.reduce((set, resource) => {
    set.add(normalizedResourceKind(resource));
    return set;
  }, new Set())).sort();
}

function reservationDurationOptions(resource = {}) {
  const rules = resource?.reservationRules || {};
  const slot = Math.max(Number.parseInt(rules.slotSizeMinutes || "30", 10) || 30, 15);
  const min = Math.max(Number.parseInt(rules.minDurationMinutes || slot, 10) || slot, slot);
  const max = Math.max(Number.parseInt(rules.maxDurationMinutes || min, 10) || min, min);
  const options = [];
  for (let minutes = min; minutes <= max && options.length < 4; minutes += slot) {
    options.push(minutes);
  }
  return options.length ? options : [min];
}

function durationOptionLabel(minutes) {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hr${hours === 1 ? "" : "s"}`;
  }
  return `${minutes} min`;
}

export function MemberPortalReservationBoard({
  businessHours = null,
  context = "visits",
  loading = false,
  member = null,
  onCancelReservation,
  onCreateReservation,
  onRescheduleReservation,
  range = {},
  readinessBlocked = false,
  readinessMessage = "",
  reservations = [],
  requestedResourceId = "",
  bookingRequestNonce = 0,
  reservationEligibility = null,
  savingReservationActionId = "",
  savingReservation = false
}) {
  const workspace = workspaceNoun(range, "range");
  const compactDaily = context === "today";
  const resources = useMemo(
    () => sortMemberResources(reservationEligibility?.resources || []),
    [reservationEligibility?.resources]
  );
  const maintenanceWindows = activeMaintenanceWindows(reservationEligibility?.maintenanceWindows || []);
  const currentMaintenance = maintenanceWindows[0] || null;
  const eligibility = reservationEligibility?.eligibility || {};
  const allowed = Boolean(eligibility.allowed && member?.status === "active");
  const activeReservations = (reservations || [])
    .filter((reservation) => !["cancelled", "completed", "no_show"].includes(String(reservation.status || "").toLowerCase()))
    .sort((left, right) => Date.parse(left.startsAt || 0) - Date.parse(right.startsAt || 0));
  const todaysReservations = activeReservations.filter(reservationIsToday);
  const upcomingReservations = activeReservations.filter((reservation) => !reservationIsToday(reservation) && Date.parse(reservation.endsAt || reservation.startsAt || 0) >= Date.now());
  const otherActiveReservations = activeReservations.filter((reservation) => {
    const endsAt = Date.parse(reservation.endsAt || reservation.startsAt || 0);
    return !reservationIsToday(reservation) && (!Number.isFinite(endsAt) || endsAt < Date.now());
  });
  const reservationResourceIdsByName = activeReservations.reduce((map, reservation) => {
    const name = memberReservationResourceName(reservation);
    const resourceKey = reservation.resourceId || reservation.resource?.resourceId || reservation.resourceSnapshot?.resourceId || reservation.reservationId || name;
    if (!map.has(name)) {
      map.set(name, []);
    }
    if (!map.get(name).includes(resourceKey)) {
      map.get(name).push(resourceKey);
    }
    return map;
  }, new Map());
  function displayReservationResourceName(reservation = {}) {
    const name = memberReservationResourceName(reservation);
    const resourceKey = reservation.resourceId || reservation.resource?.resourceId || reservation.resourceSnapshot?.resourceId || reservation.reservationId || name;
    const matches = reservationResourceIdsByName.get(name) || [];
    if (matches.length <= 1) {
      return name;
    }
    const index = Math.max(0, matches.indexOf(resourceKey));
    return `${name} ${index + 1}`;
  }
  const resourceCategories = categorizeMemberResources(resources, readinessBlocked, allowed, businessHours);
  const reservableResources = resourceCategories.reservable;
  const unavailableResources = [...resourceCategories.restricted, ...resourceCategories.unavailable];
  const visibleResources = useMemo(() => (resources.length ? resources : []), [resources]);
  const listedResources = compactDaily
    ? reservableResources.slice(0, 3)
    : (reservableResources.length ? reservableResources : unavailableResources);
  const visibleResourceIdsByName = visibleResources.reduce((map, resource) => {
    const name = memberResourceDisplayName(resource);
    const resourceKey = resource.resourceId || resource.name || name;
    if (!map.has(name)) {
      map.set(name, []);
    }
    if (!map.get(name).includes(resourceKey)) {
      map.get(name).push(resourceKey);
    }
    return map;
  }, new Map());
  function displayBoardResourceName(resource = {}) {
    const name = memberResourceDisplayName(resource);
    const resourceKey = resource.resourceId || resource.name || name;
    const matches = visibleResourceIdsByName.get(name) || [];
    if (matches.length <= 1) {
      return name;
    }
    const index = Math.max(0, matches.indexOf(resourceKey));
    return `${name} ${index + 1}`;
  }
  const firstResourceId = reservableResources[0]?.resourceId || listedResources[0]?.resourceId || "";
  const limitUsage = reservationLimitUsage(activeReservations, eligibility);
  const limitReached = Boolean(!readinessBlocked && limitUsage.reached);
  const [selectedResourceId, setSelectedResourceId] = useState(firstResourceId);
  const selectedResource = visibleResources.find((resource) => resource.resourceId === selectedResourceId) || reservableResources[0] || listedResources[0] || null;
  const defaultWindow = defaultMemberReservationWindow(selectedResource || {}, businessHours);
  const [form, setForm] = useState({
    startsAt: defaultWindow.startsAt,
    endsAt: defaultWindow.endsAt,
    notes: ""
  });
  const [formError, setFormError] = useState("");
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState("");
  const [editingReservationId, setEditingReservationId] = useState("");
  const [editForm, setEditForm] = useState({ startsAt: "", endsAt: "", notes: "" });
  const [editError, setEditError] = useState("");
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourceKindFilter, setResourceKindFilter] = useState("all");
  const bookingKindOptions = useMemo(() => resourceKindOptions(listedResources), [listedResources]);
  const filteredListedResources = useMemo(
    () => listedResources.filter((resource) => resourceMatchesFilters(resource, resourceSearch, resourceKindFilter)),
    [listedResources, resourceKindFilter, resourceSearch]
  );

  useEffect(() => {
    if (!selectedResourceId && firstResourceId) {
      setSelectedResourceId(firstResourceId);
    }
  }, [firstResourceId, selectedResourceId]);

  useEffect(() => {
    if (!requestedResourceId) {
      return;
    }
    const requestedResource = visibleResources.find((resource) => resource.resourceId === requestedResourceId);
    if (!requestedResource) {
      return;
    }
    setSelectedResourceId(requestedResourceId);
    setResourceSearch(memberResourceDisplayName(requestedResource));
    setResourceKindFilter("all");
    setBookingOpen(true);
  }, [bookingRequestNonce, requestedResourceId, visibleResources]);

  useEffect(() => {
    if (!selectedResource) {
      return;
    }
    const nextWindow = defaultMemberReservationWindow(selectedResource, businessHours);
    setForm((current) => ({
      ...current,
      startsAt: nextWindow.startsAt,
      endsAt: nextWindow.endsAt
    }));
  }, [businessHours, selectedResource]);

  const selectedCanReserve = Boolean(
    allowed &&
    !readinessBlocked &&
    !limitReached &&
    selectedResource &&
    selectedResource.reservable !== false &&
    selectedResource.reservationRules?.enabled &&
    selectedResource.memberAvailability?.canReserve !== false &&
    resourceHasReservableWindow(selectedResource, new Date(), businessHours)
  );
  const inputConstraints = memberReservationInputConstraints(selectedResource || {}, businessHours, form.startsAt);

  function getReservationId(reservation = {}) {
    return reservation.reservationId || `${reservation.resourceId}-${reservation.startsAt}`;
  }

  function reservationResource(reservation = {}) {
    const resourceId = reservation.resourceId || reservation.resource?.resourceId || reservation.resourceSnapshot?.resourceId || "";
    return visibleResources.find((resource) => resource.resourceId === resourceId) ||
      reservation.resource ||
      reservation.resourceSnapshot ||
      null;
  }

  function handleStartChange(value) {
    const nextConstraints = memberReservationInputConstraints(selectedResource || {}, businessHours, value);
    setForm((current) => {
      let nextEndsAt = current.endsAt;
      const currentEnd = nextEndsAt ? new Date(nextEndsAt) : null;
      const minEnd = nextConstraints.endMin ? new Date(nextConstraints.endMin) : null;
      const maxEnd = nextConstraints.endMax ? new Date(nextConstraints.endMax) : null;
      if (!currentEnd || Number.isNaN(currentEnd.getTime()) || (minEnd && currentEnd < minEnd)) {
        nextEndsAt = nextConstraints.endMin || nextEndsAt;
      } else if (maxEnd && currentEnd > maxEnd) {
        nextEndsAt = nextConstraints.endMax || nextEndsAt;
      }
      return {
        ...current,
        startsAt: value,
        endsAt: nextEndsAt
      };
    });
  }

  function handleEditStartChange(reservation, value) {
    const resource = reservationResource(reservation) || {};
    const nextConstraints = memberReservationInputConstraints(resource, businessHours, value);
    setEditForm((current) => {
      let nextEndsAt = current.endsAt;
      const currentEnd = nextEndsAt ? new Date(nextEndsAt) : null;
      const minEnd = nextConstraints.endMin ? new Date(nextConstraints.endMin) : null;
      const maxEnd = nextConstraints.endMax ? new Date(nextConstraints.endMax) : null;
      if (!currentEnd || Number.isNaN(currentEnd.getTime()) || (minEnd && currentEnd < minEnd)) {
        nextEndsAt = nextConstraints.endMin || nextEndsAt;
      } else if (maxEnd && currentEnd > maxEnd) {
        nextEndsAt = nextConstraints.endMax || nextEndsAt;
      }
      return {
        ...current,
        startsAt: value,
        endsAt: nextEndsAt
      };
    });
  }

  function applyDuration(minutes) {
    const startsAt = form.startsAt ? new Date(form.startsAt) : new Date();
    if (!Number.isFinite(startsAt.getTime())) {
      return;
    }
    setForm((current) => ({
      ...current,
      endsAt: toDateTimeLocalInput(addMinutes(startsAt, minutes))
    }));
  }

  function applyEditDuration(minutes) {
    const startsAt = editForm.startsAt ? new Date(editForm.startsAt) : new Date();
    if (!Number.isFinite(startsAt.getTime())) {
      return;
    }
    setEditForm((current) => ({
      ...current,
      endsAt: toDateTimeLocalInput(addMinutes(startsAt, minutes))
    }));
  }

  function openReservationManager(reservation = {}) {
    const reservationId = getReservationId(reservation);
    setSelectedReservationId((current) => current === reservationId ? "" : reservationId);
    setEditingReservationId("");
    setEditError("");
  }

  function openReservationEdit(reservation = {}) {
    const reservationId = getReservationId(reservation);
    setSelectedReservationId(reservationId);
    setEditingReservationId(reservationId);
    setEditError("");
    setEditForm({
      startsAt: reservation.startsAt ? toDateTimeLocalInput(new Date(reservation.startsAt)) : "",
      endsAt: reservation.endsAt ? toDateTimeLocalInput(new Date(reservation.endsAt)) : "",
      notes: reservation.notes || ""
    });
  }

  async function handleCancelReservation(reservation = {}) {
    const reservationId = getReservationId(reservation);
    if (!reservationId || !onCancelReservation) {
      return;
    }
    await onCancelReservation(reservationId, { reason: "Cancelled by member." });
    setSelectedReservationId("");
    setEditingReservationId("");
  }

  async function handleRescheduleSubmit(event, reservation = {}) {
    event.preventDefault();
    setEditError("");
    const reservationId = getReservationId(reservation);
    const resource = reservationResource(reservation);
    if (!reservationId || !resource) {
      setEditError("Choose a valid reservation.");
      return;
    }
    const startsAt = new Date(editForm.startsAt);
    const endsAt = new Date(editForm.endsAt);
    if (!editForm.startsAt || !editForm.endsAt || Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
      setEditError("Choose a valid start and end time.");
      return;
    }
    if (endsAt <= startsAt) {
      setEditError("End time must be after start time.");
      return;
    }
    const durationMinutes = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
    const rules = resource.reservationRules || {};
    const min = Number.parseInt(rules.minDurationMinutes || "0", 10) || 0;
    const max = Number.parseInt(rules.maxDurationMinutes || "0", 10) || 0;
    const slot = Number.parseInt(rules.slotSizeMinutes || "0", 10) || 0;
    if (min && durationMinutes < min) {
      setEditError(`Reservation must be at least ${min} minutes.`);
      return;
    }
    if (max && durationMinutes > max) {
      setEditError(`Reservation cannot be longer than ${max} minutes.`);
      return;
    }
    if (slot && durationMinutes % slot !== 0) {
      setEditError(`Reservation duration must align to ${slot}-minute slots.`);
      return;
    }
    await onRescheduleReservation?.(reservationId, {
      resourceId: resource.resourceId || reservation.resourceId,
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      notes: editForm.notes
    });
    setEditingReservationId("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError("");
    if (!selectedResource) {
      setFormError("Choose a resource before reserving.");
      return;
    }
    const startsAt = new Date(form.startsAt);
    const endsAt = new Date(form.endsAt);
    if (!form.startsAt || !form.endsAt || Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
      setFormError("Choose a valid start and end time.");
      return;
    }
    if (endsAt <= startsAt) {
      setFormError("End time must be after start time.");
      return;
    }
    const durationMinutes = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
    const rules = selectedResource.reservationRules || {};
    const min = Number.parseInt(rules.minDurationMinutes || "0", 10) || 0;
    const max = Number.parseInt(rules.maxDurationMinutes || "0", 10) || 0;
    const slot = Number.parseInt(rules.slotSizeMinutes || "0", 10) || 0;
    if (min && durationMinutes < min) {
      setFormError(`Reservation must be at least ${min} minutes.`);
      return;
    }
    if (max && durationMinutes > max) {
      setFormError(`Reservation cannot be longer than ${max} minutes.`);
      return;
    }
    if (slot && durationMinutes % slot !== 0) {
      setFormError(`Reservation duration must align to ${slot}-minute slots.`);
      return;
    }
    const result = await onCreateReservation?.({
      resourceId: selectedResource.resourceId,
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      notes: form.notes
    });
    if (!result || result.ok !== false) {
      setBookingOpen(false);
      setSelectedReservationId("");
    }
  }

  function renderReservationCard(reservation = {}, options = {}) {
    const reservationId = getReservationId(reservation);
    const selected = selectedReservationId === reservationId;
    const editing = editingReservationId === reservationId;
    const resource = reservationResource(reservation) || {};
    const constraints = memberReservationInputConstraints(resource, businessHours, editForm.startsAt);
    const actionBusy = savingReservationActionId === reservationId;
    const blocked = Boolean(options.blocked || readinessBlocked);
    const canManage = Boolean(!blocked && (onCancelReservation || onRescheduleReservation));
    return (
      <article className={`${blocked ? "is-blocked" : ""} ${selected ? "is-selected" : ""}`} key={reservationId}>
        <div className="member-reservation-card-main">
          <div>
            <strong>{displayReservationResourceName(reservation)}</strong>
            <span>{formatPublicEventDate(reservation.startsAt)} - {formatPublicEventDate(reservation.endsAt)}</span>
            <em>{blocked ? "Blocked until release form is signed" : publicReservationStatusLabel(reservation.status)}</em>
          </div>
          {canManage && (
            <button
              className="secondary-button"
              onClick={() => openReservationManager(reservation)}
              type="button"
            >
              {selected ? "Close" : "Manage"}
            </button>
          )}
        </div>
        {blocked && <a className="public-primary-link" href="#member-documents">{RELEASE_FORM_ACTION_LABEL}</a>}
        {selected && !blocked && (
          <div className="member-reservation-manage-panel">
            {!editing ? (
              <div className="member-reservation-action-row">
                <button
                  className="secondary-button"
                  disabled={!onRescheduleReservation || actionBusy}
                  onClick={() => openReservationEdit(reservation)}
                  type="button"
                >
                  Move time
                </button>
                <button
                  className="secondary-button is-danger"
                  disabled={!onCancelReservation || actionBusy}
                  onClick={() => handleCancelReservation(reservation)}
                  type="button"
                >
                  {actionBusy ? "Cancelling..." : "Cancel booking"}
                </button>
              </div>
            ) : (
              <form className="member-reservation-edit-form" onSubmit={(event) => handleRescheduleSubmit(event, reservation)}>
                <label>
                  <span>From</span>
                  <input
                    aria-label="Move booking start time"
                    min={constraints.startMin}
                    step={constraints.stepSeconds}
                    type="datetime-local"
                    value={editForm.startsAt}
                    onInput={(event) => handleEditStartChange(reservation, event.currentTarget.value)}
                    onChange={(event) => handleEditStartChange(reservation, event.target.value)}
                  />
                </label>
                <label>
                  <span>To</span>
                  <input
                    aria-label="Move booking end time"
                    max={constraints.endMax}
                    min={constraints.endMin}
                    step={constraints.stepSeconds}
                    type="datetime-local"
                    value={editForm.endsAt}
                    onChange={(event) => setEditForm((current) => ({ ...current, endsAt: event.target.value }))}
                  />
                </label>
                <div className="member-duration-button-row" aria-label="Reservation duration">
                  {reservationDurationOptions(resource).map((minutes) => (
                    <button
                      className="secondary-button"
                      key={minutes}
                      onClick={() => applyEditDuration(minutes)}
                      type="button"
                    >
                      {durationOptionLabel(minutes)}
                    </button>
                  ))}
                </div>
                <label>
                  <span>Note</span>
                  <textarea
                    aria-label="Move booking note"
                    value={editForm.notes}
                    onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>
                {editError && <div className="notice is-error">{editError}</div>}
                <div className="member-reservation-action-row">
                  <button className="public-primary-link" disabled={actionBusy || !onRescheduleReservation} type="submit">
                    {actionBusy ? "Saving..." : "Save time"}
                  </button>
                  <button className="secondary-button" onClick={() => setEditingReservationId("")} type="button">
                    Cancel edit
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </article>
    );
  }

  return (
    <section className="member-reservation-board" aria-label="Daily reservations">
      <div className="member-reservation-board-header">
        <div>
          <span className="public-section-kicker">{compactDaily ? `Today at the ${workspace}` : "Reservations"}</span>
          <h3>{compactDaily ? (readinessBlocked ? "Not ready yet" : "Today’s range plan") : "Reserve a time"}</h3>
          <p>
            {compactDaily
              ? readinessBlocked
                ? "Sign the release form before visiting."
                : "Today’s access, bookings, and next step."
              : readinessBlocked
                ? "Finish the required step before booking."
                : "Manage existing reservations or book a new time."}
          </p>
        </div>
        {!compactDaily && (
          <div className="member-booking-summary-chip">
            <span>{limitUsage.detail}</span>
            <strong>{reservableResources.length ? `${reservableResources.length} open` : "No online areas"}</strong>
          </div>
        )}
      </div>

      <div className="member-range-opening-strip">
        <div>
          <span>Range status</span>
          <strong>{readinessBlocked ? "Not ready yet" : rangeOpeningStatusLabel(businessHours, allowed ? "Open to eligible members" : "Booking unavailable")}</strong>
          <small>{readinessBlocked ? readinessMessage : businessHours ? resourceOpeningTimeLabel({}, businessHours) : "Range hours"}</small>
        </div>
        <div>
          <span>Areas</span>
          <strong>{visibleResources.length ? (readinessBlocked ? `${reservableResources.length} unlock after signing` : limitReached ? `${reservableResources.length} after limit frees` : `${reservableResources.length} can be reserved`) : "None open"}</strong>
          <small>{visibleResources.length ? `${resourceCategories.restricted.length} restricted / ${resourceCategories.unavailable.length} unavailable` : "No online booking"}</small>
        </div>
        <div>
          <span>Limit</span>
          <strong>{limitUsage.detail}</strong>
          <small>{reservationAdvanceWindowLabel(eligibility)} / {limitUsage.label}</small>
        </div>
        <div>
          <span>Closures</span>
          <strong>{currentMaintenance ? maintenanceWindowTitle(currentMaintenance) : "No closures posted"}</strong>
          <small>{currentMaintenance ? `${formatPublicEventDate(currentMaintenance.startsAt)} - ${formatPublicEventDate(currentMaintenance.endsAt)}` : "Next 30 days clear"}</small>
        </div>
      </div>

      <div className={compactDaily ? "member-daily-reservation-flow" : "member-visits-flow"}>
      <div className="member-visits-reservations-panel">
      <div className="member-booking-priority" aria-label="Your reservations">
        <div className="member-booking-priority-heading">
          <div>
            <span>Your reservations</span>
            <strong>{activeReservations.length ? `${activeReservations.length} active booking${activeReservations.length === 1 ? "" : "s"}` : "No active bookings"}</strong>
            <small>{activeReservations.length ? "Active bookings count toward your limit." : "New bookings appear here."}</small>
          </div>
          {selectedCanReserve && (
            <button className="secondary-button" onClick={() => setBookingOpen(true)} type="button">
              {activeReservations.length ? "Reserve another" : "Reserve a visit"}
            </button>
          )}
        </div>
        <div className={`member-booking-reservation-columns ${compactDaily ? "is-compact" : ""}`}>
          <section>
            <div>
              <span>Today</span>
              <strong>{todaysReservations.length || "None today"}</strong>
              <small>{todaysReservations.length ? (readinessBlocked ? "Blocked until release form is signed." : "Active today.") : "Same-day bookings appear here."}</small>
            </div>
            {todaysReservations.slice(0, compactDaily ? 2 : 3).map((reservation) => renderReservationCard(reservation, { blocked: readinessBlocked }))}
            {compactDaily && todaysReservations.length > 2 && (
              <article className={readinessBlocked ? "is-blocked" : ""}>
                <strong>{todaysReservations.length - 2} more today</strong>
                <span>{readinessBlocked ? "Finish signing to use them." : "View the full list here."}</span>
                <em>{readinessBlocked ? "Blocked until release form is signed" : "Booked"}</em>
              </article>
            )}
          </section>
          {!compactDaily && (
            <section>
              <div>
                <span>Upcoming</span>
                <strong>{upcomingReservations.length || "None scheduled"}</strong>
                <small>{upcomingReservations.length ? "Future bookings." : "Future bookings appear here."}</small>
              </div>
              {upcomingReservations.slice(0, 4).map((reservation) => renderReservationCard(reservation))}
              {upcomingReservations.length > 4 && (
                <article>
                  <strong>{upcomingReservations.length - 4} more scheduled</strong>
                  <span>Use the list to manage later bookings.</span>
                  <em>Upcoming</em>
                </article>
              )}
            </section>
          )}
          {!compactDaily && otherActiveReservations.length > 0 && (
            <section>
              <div>
                <span>Other active</span>
                <strong>{otherActiveReservations.length} counting toward limit</strong>
                <small>Still count toward your limit.</small>
              </div>
              {otherActiveReservations.slice(0, 4).map((reservation) => renderReservationCard(reservation))}
            </section>
          )}
        </div>
      </div>
      </div>

      <div className="member-visits-reserve-panel">
      {compactDaily && (
        <div className="member-reservation-empty-state">
          <div>
          <span>{readinessBlocked ? "Finish first" : reservableResources.length ? "Ready to reserve" : "Booking closed"}</span>
            <strong>
              {readinessBlocked
                ? readinessMessage
                : reservableResources.length
                ? `${reservableResources.length} area${reservableResources.length === 1 ? "" : "s"} can be reserved online.`
                : "No online booking areas."}
            </strong>
            <small>
              {readinessBlocked
                ? "Release form required."
                : reservableResources.length
                ? "Choose an area and time below."
                : "Check back when reservations open."}
            </small>
          </div>
          {(readinessBlocked || !reservableResources.length) && (
            <a className="secondary-button" href={readinessBlocked ? "#member-documents" : "#member-visits"}>
              {readinessBlocked ? RELEASE_FORM_ACTION_LABEL : "Check visits"}
            </a>
          )}
        </div>
      )}

      {visibleResources.length === 0 || (compactDaily && reservableResources.length === 0) ? (
        <div className="member-reservation-empty-state">
          <div>
            <span>{visibleResources.length ? "No open times" : "Booking closed"}</span>
            <strong>{visibleResources.length ? "No future window is open." : "No online areas are open."}</strong>
            <small>
              {visibleResources.length
                ? "Future times appear when published."
                : "Areas appear here when reservations open."}
            </small>
          </div>
          <a className="secondary-button" href="#member-support">Ask the {workspace}</a>
        </div>
      ) : !bookingOpen ? (
        <div className="member-booking-start-panel">
          <div>
            <span>{limitReached ? "Limit reached" : reservableResources.length ? "Ready" : "Unavailable"}</span>
            <strong>
              {limitReached
                ? limitUsage.detail
                : reservableResources.length
                  ? "Choose an area and time."
                  : "No online areas are open."}
            </strong>
            <small>
              {limitReached
                ? "Cancel or complete a booking first."
                : reservableResources.length
                  ? `${reservableResources.length} reservable`
                  : "Check again later."}
            </small>
          </div>
          <button className="public-primary-link" disabled={!selectedCanReserve} onClick={() => setBookingOpen(true)} type="button">
            {limitReached ? "Review bookings" : "Start booking"}
          </button>
        </div>
      ) : (
      <>
      <div className="member-book-another-heading">
        <span>{activeReservations.length ? "Reserve another" : "Reserve"}</span>
        <strong>{limitReached ? "Limit reached" : reservableResources.length ? "Choose area and time" : "No area open"}</strong>
        <small>{limitReached ? limitUsage.detail : reservableResources.length ? "Resource first, then time." : "Check again later."}</small>
        <button className="secondary-button" onClick={() => setBookingOpen(false)} type="button">Close booking</button>
      </div>
      <div className="member-resource-reservation-grid">
        <div className="member-resource-list">
          <div className="member-resource-list-heading">
            <h4>Area</h4>
            <span>{filteredListedResources.length} shown</span>
          </div>
          <div className="member-resource-filter-row" aria-label="Filter bookable areas">
            <label>
              <span>Search</span>
              <input
                aria-label="Search bookable areas"
                type="search"
                value={resourceSearch}
                onChange={(event) => setResourceSearch(event.target.value)}
                placeholder="Lane, bay, clay..."
              />
            </label>
            {bookingKindOptions.length > 1 && (
              <label>
                <span>Type</span>
                <select aria-label="Bookable area type" value={resourceKindFilter} onChange={(event) => setResourceKindFilter(event.target.value)}>
                  <option value="all">All types</option>
                  {bookingKindOptions.map((kind) => (
                    <option key={kind} value={kind}>{kind.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {loading ? (
            <div className="compact-status-row">Refreshing resources...</div>
          ) : listedResources.length === 0 ? (
            <div className="compact-status-row">No member booking areas are open yet.</div>
          ) : filteredListedResources.length === 0 ? (
            <div className="compact-status-row">No areas match that search.</div>
          ) : filteredListedResources.map((resource, index) => {
            const canReserve = Boolean(!readinessBlocked && !limitReached && allowed && resource.memberAvailability?.canReserve !== false && resource.reservationRules?.enabled && resourceHasReservableWindow(resource, new Date(), businessHours));
            const displayName = displayBoardResourceName(resource);
            return (
              <button
                className={selectedResource?.resourceId === resource.resourceId ? "is-selected" : ""}
                key={resource.resourceId || resource.name}
                onClick={() => setSelectedResourceId(resource.resourceId)}
                type="button"
              >
                <span>{readinessBlocked ? "Blocked until release form is signed" : resourceOpeningStatusLabel(resource)}</span>
                <strong>{displayName}</strong>
                <small>{memberResourceKindLabel(resource)} · {resourceOpeningTimeLabel(resource, businessHours)}</small>
                <em>{canReserve ? "Select" : readinessBlocked ? "Finish first" : limitReached ? "Limit reached" : memberResourceUnavailableReason(resource)}</em>
              </button>
            );
          })}
        </div>

        {readinessBlocked ? (
          <div className="member-reservation-form member-reservation-blocked-panel">
            <div>
              <span>Booking paused</span>
              <strong>{readinessMessage || "Finish the required member step."}</strong>
              <small>New reservations unlock afterward.</small>
            </div>
            <a className="public-primary-link" href="#member-documents">{RELEASE_FORM_ACTION_LABEL}</a>
          </div>
        ) : limitReached ? (
          <div className="member-reservation-form member-reservation-blocked-panel">
            <div>
              <span>Booking limit reached</span>
              <strong>{limitUsage.label}</strong>
              <small>Cancel or complete a booking first.</small>
            </div>
            <a className="secondary-button" href="#member-visits">Review bookings</a>
          </div>
        ) : (
        <form className="member-reservation-form" id="member-reserve-form" onSubmit={handleSubmit}>
          <div>
            <span>Time</span>
            <strong>{selectedResource ? displayBoardResourceName(selectedResource) : "Choose an area"}</strong>
            <small>{selectedResource ? `${memberResourceKindLabel(selectedResource)} · ${memberReservationRuleSummary(selectedResource)}` : "Select an area first."}</small>
          </div>
          {selectedResource && (
            <div className="member-reservation-rule-strip">
              <span>{resourceOpeningStatusLabel(selectedResource)}</span>
              <span>{resourceOpeningTimeLabel(selectedResource, businessHours)}</span>
              <span>{selectedResource.reservationRules?.payment?.enabled ? formatMoney(selectedResource.reservationRules.payment) : "No fee"}</span>
            </div>
          )}
          <label>
            <span>From</span>
            <input
              aria-label="Reservation start time"
              min={inputConstraints.startMin}
              step={inputConstraints.stepSeconds}
              type="datetime-local"
              value={form.startsAt}
              onInput={(event) => handleStartChange(event.currentTarget.value)}
              onChange={(event) => handleStartChange(event.target.value)}
            />
          </label>
          <label>
            <span>To</span>
            <input
              aria-label="Reservation end time"
              max={inputConstraints.endMax}
              min={inputConstraints.endMin}
              step={inputConstraints.stepSeconds}
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => setForm((current) => ({ ...current, endsAt: event.target.value }))}
            />
          </label>
          {selectedResource && (
            <div className="member-duration-button-row" aria-label="Reservation duration">
              {reservationDurationOptions(selectedResource).map((minutes) => (
                <button
                  className="secondary-button"
                  key={minutes}
                  onClick={() => applyDuration(minutes)}
                  type="button"
                >
                  {durationOptionLabel(minutes)}
                </button>
              ))}
            </div>
          )}
          <label>
            <span>Note</span>
            <textarea
              aria-label="Reservation note"
              value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              placeholder="Optional"
            />
          </label>
          {formError && <div className="notice is-error">{formError}</div>}
          {(readinessBlocked || !allowed) && (
            <div className="compact-status-row">
              {readinessBlocked
                ? readinessMessage
                : eligibility.reasons?.includes("active_membership_required")
                ? "An active membership is required before booking."
                : eligibility.reasons?.includes("membership_plan_not_eligible")
                  ? "This membership plan does not include reservations."
                  : `Member reservations are not enabled for this ${workspace} yet.`}
            </div>
          )}
          {selectedResource && selectedResource.memberAvailability?.canReserve === false && (
            <div className="compact-status-row">{memberResourceUnavailableReason(selectedResource)}</div>
          )}
          <div className={`member-reservation-limit-note ${limitReached ? "is-reached" : ""}`}>
            <span>{limitUsage.detail}</span>
            <small>{limitUsage.label}</small>
          </div>
          <button type="submit" className="public-primary-link" disabled={!selectedCanReserve || savingReservation}>
            {savingReservation ? "Reserving..." : "Reserve"}
          </button>
        </form>
        )}
      </div>
      </>
      )}
      </div>
      </div>
    </section>
  );
}


export function MemberPortalVisitsPanel({
  businessHours = null,
  loading = false,
  member = null,
  onCancelReservation,
  onCreateReservation,
  onRescheduleReservation,
  range = {},
  readinessBlocked = false,
  readinessMessage = "",
  reservations = [],
  reservationEligibility = null,
  savingReservationActionId = "",
  savingReservation = false
}) {
  const [visitsSection, setVisitsSection] = useState("bookings");
  const [requestedResourceId, setRequestedResourceId] = useState("");
  const [bookingRequestNonce, setBookingRequestNonce] = useState(0);
  const [areaSearch, setAreaSearch] = useState("");
  const [areaKindFilter, setAreaKindFilter] = useState("all");
  const bookableResources = reservationEligibility?.resources || [];
  const activeReservations = reservations.filter((reservation) => !["cancelled", "completed", "no_show"].includes(String(reservation.status || "").toLowerCase()));
  const maintenanceWindows = activeMaintenanceWindows(reservationEligibility?.maintenanceWindows || []);
  const eligibility = reservationEligibility?.eligibility || {};
  const allowed = Boolean(eligibility.allowed && member?.status === "active");
  const resourceCategories = categorizeMemberResources(bookableResources, readinessBlocked, allowed, businessHours);
  const visibleResources = useMemo(
    () => sortMemberResources([
      ...resourceCategories.reservable,
      ...resourceCategories.unavailable,
      ...resourceCategories.restricted
    ]),
    [resourceCategories.reservable, resourceCategories.restricted, resourceCategories.unavailable]
  );
  const visibleResourceNameCounts = visibleResources.reduce((map, resource) => {
    const name = memberResourceDisplayName(resource);
    map.set(name, (map.get(name) || 0) + 1);
    return map;
  }, new Map());
  const areaKindOptions = useMemo(() => resourceKindOptions(visibleResources), [visibleResources]);
  const filteredVisibleResources = useMemo(
    () => visibleResources.filter((resource) => resourceMatchesFilters(resource, areaSearch, areaKindFilter)),
    [areaKindFilter, areaSearch, visibleResources]
  );

  function requestResourceBooking(resourceId = "") {
    if (!resourceId) {
      return;
    }
    setRequestedResourceId(resourceId);
    setBookingRequestNonce((current) => current + 1);
    setVisitsSection("bookings");
  }

  return (
    <section className="member-portal-visits-panel" id="member-visits">
      <MemberPortalSectionTabs
        activeSection={visitsSection}
        onSelect={setVisitsSection}
        sections={[
          { id: "bookings", label: "Reservations", detail: "Book/manage" },
          { id: "areas", label: "Areas", detail: "Lanes and bays" },
          { id: "closures", label: "Closures", detail: "Maintenance" },
          { id: "gates", label: "Rules", detail: "Limits" }
        ]}
      />

      {visitsSection === "bookings" && (
        <MemberPortalReservationBoard
          businessHours={businessHours}
          context="visits"
          loading={loading}
          member={member}
          onCancelReservation={onCancelReservation}
          onCreateReservation={onCreateReservation}
          onRescheduleReservation={onRescheduleReservation}
          range={range}
          readinessBlocked={readinessBlocked}
          readinessMessage={readinessMessage}
          reservations={reservations}
          requestedResourceId={requestedResourceId}
          bookingRequestNonce={bookingRequestNonce}
          reservationEligibility={reservationEligibility}
          savingReservationActionId={savingReservationActionId}
          savingReservation={savingReservation}
        />
      )}

      {visitsSection === "areas" && (
        <div className="member-portal-split-list">
        <div className="member-area-list-panel">
          <div className="member-resource-list-heading">
            <h3>Areas</h3>
            <span>{filteredVisibleResources.length} shown</span>
          </div>
          <div className="member-resource-filter-row" aria-label="Filter available areas">
            <label>
              <span>Search</span>
              <input
                aria-label="Search available areas"
                type="search"
                value={areaSearch}
                onChange={(event) => setAreaSearch(event.target.value)}
                placeholder="Lane, bay, clay..."
              />
            </label>
            {areaKindOptions.length > 1 && (
              <label>
                <span>Type</span>
                <select aria-label="Area type" value={areaKindFilter} onChange={(event) => setAreaKindFilter(event.target.value)}>
                  <option value="all">All types</option>
                  {areaKindOptions.map((kind) => (
                    <option key={kind} value={kind}>{kind.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {visibleResources.length === 0 ? (
            <div className="compact-status-row">No areas published yet.</div>
          ) : filteredVisibleResources.length === 0 ? (
            <div className="compact-status-row">No areas match that search.</div>
          ) : filteredVisibleResources.map((resource, index) => {
            const resourceName = memberResourceDisplayName(resource);
            const displayName = visibleResourceNameCounts.get(resourceName) > 1
              ? `${resourceName} ${index + 1}`
              : resourceName;
            const canReserve = resourceCategories.reservable.some((item) => item.resourceId === resource.resourceId);
            return (
              <article className="member-area-card" key={resource.resourceId || resource.name || resource.label}>
                <div>
                  <strong>{displayName}</strong>
                  <span>{memberResourceKindLabel(resource)} · {resourceOpeningTimeLabel(resource, businessHours)}</span>
                  <em>{canReserve ? "Reservable" : memberResourceUnavailableReason(resource)}</em>
                </div>
                {canReserve && (
                  <button
                    className="secondary-button"
                    onClick={() => requestResourceBooking(resource.resourceId)}
                    type="button"
                  >
                    Book this area
                  </button>
                )}
              </article>
            );
          })}
        </div>
        <div>
          <h3>Availability</h3>
          <article>
            <strong>{resourceCategories.reservable.length} reservable</strong>
            <span>{resourceCategories.unavailable.length} unavailable / {resourceCategories.restricted.length} restricted</span>
            <em>Now</em>
          </article>
          <article>
            <strong>{reservationLimitUsage(activeReservations, eligibility).label}</strong>
            <span>{reservationLimitUsage(activeReservations, eligibility).detail}</span>
            <em>Allowance</em>
          </article>
        </div>
      </div>
      )}

      {visitsSection === "closures" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Closures</h3>
            {maintenanceWindows.length === 0 ? (
              <article>
                <strong>No closures posted</strong>
                <span>Next 30 days clear.</span>
                <em>Schedule</em>
              </article>
            ) : maintenanceWindows.map((window) => (
              <article key={window.windowId || window.maintenanceId || `${window.startsAt}-${window.endsAt}`}>
                <strong>{maintenanceWindowTitle(window)}</strong>
                <span>{formatPublicEventDate(window.startsAt)} - {formatPublicEventDate(window.endsAt)}</span>
                <em>{window.resourceName || window.resourceLabel || "Schedule"}</em>
              </article>
            ))}
          </div>
          <div>
            <h3>Impact</h3>
            <article>
              <strong>Closed times are blocked</strong>
              <span>The reservation picker skips posted closures.</span>
              <em>Booking</em>
            </article>
            <article>
              <strong>Changes appear here</strong>
              <span>Check closures before heading out.</span>
              <em>Planning</em>
            </article>
          </div>
        </div>
      )}

      {visitsSection === "gates" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Access</h3>
            <article>
              <strong>{member?.status === "active" ? "Active member" : member ? formatReservationAccessLabel(member) : "Membership needed"}</strong>
              <span>{member?.status === "active" ? "Eligible for published areas." : "Membership review required."}</span>
              <em>Access</em>
            </article>
            <article>
              <strong>Restricted areas</strong>
              <span>Missing qualifications appear on each area.</span>
              <em>Access</em>
            </article>
          </div>
          <div>
            <h3>Limits</h3>
            <article>
              <strong>{reservationLimitUsage(activeReservations, eligibility).label}</strong>
              <span>{reservationLimitUsage(activeReservations, eligibility).detail}</span>
              <em>Limit</em>
            </article>
            <article>
              <strong>{reservationAdvanceWindowLabel(eligibility)}</strong>
              <span>Future reservations follow this window.</span>
              <em>Advance booking</em>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
